import type * as mapboxgl from 'mapbox-gl';
import type { FeatureCollection } from 'geojson';
import type {
  GeoServiceFinding,
  GeometryValidationResponse,
  TracesGeometryResponse,
} from '../../../common/service/geo-upload.service';
import type { MapSelectData } from './map-select.models';
import { HEAVY_FEATURE_LIMIT } from './map-editor.constants';
import { GeoCheckFinding, isPrecisionCode } from './traces-finding-layer';
import { TracesGeometryLayerController } from './traces-geometry-layer';
import { TracesGeoState, watchTracesPending } from './traces-pending-poll';

export const TRACES_PREVIEW_DELAY_MS = 1500;
export const TRACES_PREVIEW_HEAVY_DELAY_MS = 5000;

export type TracesPreviewStatus = 'idle' | 'waiting' | 'computing' | 'failed' | 'held';

export interface TracesPreviewSource {
  derive: (site: FeatureCollection) => Promise<TracesGeometryResponse>;
  validate: (traces: FeatureCollection) => Promise<GeometryValidationResponse>;
}

export interface MapTracesGeoContext {
  data: MapSelectData;
  runInZone: (update: () => void) => void;
  changed: () => void;
  preview: TracesPreviewSource;
  findingsChanged: (findings: GeoCheckFinding[]) => void;
}

interface TracesPreview {
  geometry: FeatureCollection | undefined;
  findings: GeoCheckFinding[];
}

function previewDelay(featureCount: number): number {
  return featureCount > HEAVY_FEATURE_LIMIT ? TRACES_PREVIEW_HEAVY_DELAY_MS : TRACES_PREVIEW_DELAY_MS;
}

export function parseFeatureCollection(json: string | null | undefined): FeatureCollection | undefined {
  if (!json) return undefined;
  try {
    const parsed = JSON.parse(json);
    return parsed?.type === 'FeatureCollection' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

async function derivePreview(source: TracesPreviewSource, site: FeatureCollection): Promise<TracesPreview> {
  if (!site.features.length) return { geometry: undefined, findings: [] };
  const derived = await source.derive(site);
  const geometry = parseFeatureCollection(derived.geoJson);
  const onTraces = geometry ? ((await source.validate(geometry)).findings ?? []) : [];
  const findings = [...onTraces, ...(derived.findings ?? [])]
    .filter((finding) => isPrecisionCode(finding.code))
    .map(asGeoCheckFinding);
  return { geometry, findings };
}

function asGeoCheckFinding({ code, featureIndex, sourceFeature, at }: GeoServiceFinding): GeoCheckFinding {
  return { code, featureIndex: featureIndex ?? undefined, sourceFeature, at: at ?? undefined };
}

export class MapTracesGeoController {
  status: TracesPreviewStatus = 'idle';
  geometry: FeatureCollection | undefined;
  private layer: TracesGeometryLayerController | null = null;
  private stopTracesPendingWatch: (() => void) | null = null;
  private previewTimer: ReturnType<typeof setTimeout> | null = null;
  private previewRun = 0;
  private destroyed = false;

  constructor(private readonly context: MapTracesGeoContext) {}

  attach(map: mapboxgl.Map): void {
    this.layer = new TracesGeometryLayerController(map as any);
    this.layer.show();
    this.applyStored(this.context.data ?? {});
    if (this.context.data?.tracesGeoPending) this.startPendingWatch();
  }

  scheduleRecompute(site: () => FeatureCollection, featureCount: number): void {
    this.startPreview();
    this.setStatus('waiting');
    this.previewTimer = setTimeout(() => void this.recompute(site()), previewDelay(featureCount));
  }

  recomputeNow(site: () => FeatureCollection): void {
    this.startPreview();
    void this.recompute(site());
  }

  hold(): void {
    this.startPreview();
    this.setStatus('held');
  }

  private startPreview(): void {
    this.stopPendingWatch();
    this.cancelScheduledRecompute();
  }

  destroy(): void {
    this.destroyed = true;
    this.stopPendingWatch();
    this.cancelScheduledRecompute();
  }

  private async recompute(site: FeatureCollection): Promise<void> {
    const run = this.previewRun;
    this.setStatus('computing');
    try {
      const preview = await derivePreview(this.context.preview, site);
      if (this.isOvertaken(run)) return;
      this.showGeometry(preview.geometry);
      this.context.findingsChanged(preview.findings);
      this.setStatus('idle');
    } catch {
      if (!this.isOvertaken(run)) this.setStatus('failed');
    }
  }

  private isOvertaken(run: number): boolean {
    return this.destroyed || run !== this.previewRun;
  }

  private cancelScheduledRecompute(): void {
    this.previewRun++;
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = null;
  }

  private applyStored({ tracesGeoJson, tracesGeoPending }: TracesGeoState): void {
    this.showGeometry(parseFeatureCollection(tracesGeoJson));
    this.setStatus(tracesGeoPending ? 'computing' : 'idle');
  }

  private showGeometry(geometry: FeatureCollection | undefined): void {
    this.geometry = geometry;
    this.layer?.setData(geometry);
  }

  private setStatus(status: TracesPreviewStatus): void {
    this.status = status;
    this.context.changed();
  }

  private startPendingWatch(): void {
    const reload = this.context.data?.reloadTracesGeo;
    if (!reload) return;
    this.stopTracesPendingWatch = watchTracesPending({
      fetchSite: reload,
      onUpdate: (state) => this.context.runInZone(() => this.applyStored(state)),
    });
  }

  private stopPendingWatch(): void {
    this.stopTracesPendingWatch?.();
    this.stopTracesPendingWatch = null;
  }
}
